// ─── Gallery: Masonry ─────────────────────────────────────────
// Pinterest-style masonry layout with alternating tall/short cells.

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AnimatedSection } from '../../../lib/animations/AnimatedSection';

export default function GalleryMasonry({ section, data, theme }) {
  const anim = section.animation || {};
  const images = (data.images || []).filter(Boolean);
  const [lightboxImg, setLightboxImg] = useState(null);

  if (images.length === 0) return null;

  // Split into 2 columns with alternating heights
  const col1 = images.filter((_, i) => i % 2 === 0);
  const col2 = images.filter((_, i) => i % 2 === 1);

  const renderImg = (img, i, tall) => (
    <AnimatedSection key={i} preset={anim.items || 'zoom-soft'} delay={i * 0.06}>
      <div
        className="overflow-hidden rounded-xl cursor-pointer transition-transform hover:scale-[1.02]"
        style={{ aspectRatio: tall ? '3/4' : '4/3' }}
        onClick={() => setLightboxImg(img)}
      >
        <img src={img} alt={`Gallery photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
      </div>
    </AnimatedSection>
  );

  return (
    <>
      <section className="px-5 py-20" data-testid="gallery-section">
        <div className="max-w-md mx-auto">
          <AnimatedSection preset={anim.items || 'fade-up'}>
            <p
              className="text-xs tracking-[0.3em] uppercase mb-8 text-center"
              style={{ color: theme.accent, fontFamily: theme.eyebrowFont }}
            >
              Gallery
            </p>
          </AnimatedSection>
          <div className="flex gap-2">
            <div className="flex-1 space-y-2">
              {col1.map((img, i) => renderImg(img, i * 2, i % 2 === 0))}
            </div>
            <div className="flex-1 space-y-2 mt-6">
              {col2.map((img, i) => renderImg(img, i * 2 + 1, i % 2 !== 0))}
            </div>
          </div>
        </div>
      </section>

      {lightboxImg && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          onClick={() => setLightboxImg(null)}
        >
          <motion.img
            src={lightboxImg} alt="Gallery photo enlarged"
            className="max-w-full max-h-[85vh] rounded-lg object-contain"
            initial={{ scale: 0.9 }} animate={{ scale: 1 }}
          />
          <button className="absolute top-6 right-6 text-white/60 hover:text-white text-2xl" onClick={() => setLightboxImg(null)} aria-label="Close gallery lightbox">
            &times;
          </button>
        </motion.div>
      )}
    </>
  );
}
